import { acceptFor, MAX_FILE_BYTES } from "../../server/rules/files";
import { api, el } from "./dom";
import { icon } from "./icons";

// The upload control (brief 1A-7, mockup "Upload" and "Take photo"): pick a file or drag it in;
// on a phone, take a photo with the camera. Shows progress and says plainly what went wrong.
// The file goes up in parts (POST /api/files/uploads, PUT each part, POST /api/files), since a
// Netlify Function takes at most about 4.5 MB at a time; the server checks everything again.

/** What the server answers for a finished upload. */
export interface UploadedFile {
  id: string;
  name: string;
  type: string;
  size: number;
}

export interface UploadOptions {
  /** The record the file belongs to. */
  kind: string;
  id: string;
  /** What it is, when the place it's uploaded from says so (brief 1E-23): a file kind's id. */
  fileKind?: string;
  /** What the picker offers: any allowed file, a logo image or a font file. */
  purpose?: "any" | "logo" | "font";
  /** The button's words and icon, as in the mockup ("Upload", "Upload logo"...). */
  label?: string;
  icon?: string;
  /** Offer "Take photo" on a phone (images only). */
  camera?: boolean;
  /** A short line next to the buttons. */
  hint?: string;
  onUploaded: (file: UploadedFile) => void | Promise<void>;
}

const MB = 1024 * 1024;
const LOST = "The upload stopped. Check the connection and try again.";

/** Whether this is a phone: a touch screen or a narrow window, as the shell decides. */
export const isPhoneLike = () =>
  window.matchMedia("(pointer: coarse)").matches || window.matchMedia("(max-width:760px)").matches;

/** A save through the screens' one request helper: its request key, one resend, plain errors. */
async function postJson<T>(
  path: string,
  body: unknown,
): Promise<{ ok: boolean; body: T & { error?: string } }> {
  const res = await api<T>("POST", path, body);
  return { ok: res.ok, body: res.ok ? res.body : { ...res.body, error: res.body.error ?? LOST } };
}

/** Sends one part with progress (fetch can't report upload progress; XMLHttpRequest can). */
function sendPart(
  path: string,
  blob: Blob,
  onProgress: (sent: number) => void,
): Promise<{ ok: boolean; error?: string }> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", path);
    xhr.setRequestHeader("content-type", "application/octet-stream");
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve({ ok: true });
      let error: string | undefined;
      try {
        error = (JSON.parse(xhr.responseText) as { error?: string }).error;
      } catch {
        // Not JSON: the plain message below will do.
      }
      resolve({ ok: false, error: error ?? LOST });
    };
    xhr.onerror = () => resolve({ ok: false, error: LOST });
    xhr.send(blob);
  });
}

/**
 * Uploads one file to a record, in parts. `onProgress` gets 0 to 1. Answers the file, or why it
 * wasn't uploaded in plain words.
 */
export async function uploadFile(
  file: File,
  record: { kind: string; id: string; fileKind?: string },
  onProgress: (done: number) => void = () => undefined,
): Promise<{ ok: true; file: UploadedFile } | { ok: false; error: string }> {
  if (file.size > MAX_FILE_BYTES)
    return {
      ok: false,
      error: `That file is ${(file.size / MB).toFixed(1)} MB. Files can be up to 25 MB.`,
    };
  const start = await postJson<{ upload: string; parts: number; partBytes: number }>(
    "/api/files/uploads",
    {
      name: file.name,
      size: file.size,
      kind: record.kind,
      id: record.id,
      ...(record.fileKind ? { kindId: record.fileKind } : {}),
    },
  );
  if (!start.ok) return { ok: false, error: start.body.error ?? LOST };
  const { upload, parts, partBytes } = start.body;
  for (let n = 0; n < parts; n++) {
    const from = n * partBytes;
    const sent = await sendPart(
      `/api/files/uploads/${upload}/${n}`,
      file.slice(from, Math.min(file.size, from + partBytes)),
      (bytes) => onProgress(Math.min(1, (from + bytes) / file.size) * 0.95),
    );
    if (!sent.ok) return { ok: false, error: sent.error ?? LOST };
  }
  const done = await postJson<{ file: UploadedFile }>("/api/files", { upload });
  if (!done.ok || !done.body.file) return { ok: false, error: done.body.error ?? LOST };
  onProgress(1);
  return { ok: true, file: done.body.file };
}

/** The control: buttons, a hint, progress and a plain error. Drop a file anywhere on it. */
export function uploadControl(opts: UploadOptions): { el: HTMLElement; busy: () => boolean } {
  const purpose = opts.purpose ?? "any";
  let busy = false;
  const id = `up-${Math.random().toString(36).slice(2, 9)}`;
  const picker = el("input", { type: "file", id, accept: acceptFor(purpose), hidden: true });
  const pick = el(
    "button",
    { type: "button", class: "btn sm" },
    icon(opts.icon ?? "upload"),
    ` ${opts.label ?? "Upload"}`,
  );
  pick.addEventListener("click", () => picker.click());

  const camera = opts.camera && purpose !== "font" && isPhoneLike();
  const shot = el("input", {
    type: "file",
    id: `${id}-cam`,
    accept: "image/*",
    capture: "environment",
    hidden: true,
  });
  const take = el(
    "button",
    { type: "button", class: "btn sm" },
    icon("device-camera"),
    " Take photo",
  );
  take.addEventListener("click", () => shot.click());

  const bar = el("span", { style: "width:0%" });
  const meter = el(
    "div",
    {
      class: "meter",
      role: "progressbar",
      "aria-label": "Upload progress",
      "aria-valuemin": "0",
      "aria-valuemax": "100",
      "aria-valuenow": "0",
      hidden: true,
    },
    bar,
  );
  const said = el("p", { class: "small", role: "status", "aria-live": "polite" });
  const wrap = el(
    "div",
    { class: "upload" },
    el(
      "div",
      { class: "row" },
      pick,
      picker,
      ...(camera ? [take, shot] : []),
      ...(opts.hint ? [el("span", { class: "hint" }, opts.hint)] : []),
    ),
    meter,
    said,
  );

  const progress = (done: number) => {
    const pct = Math.round(done * 100);
    bar.style.width = `${pct}%`;
    meter.setAttribute("aria-valuenow", String(pct));
  };

  async function send(file: File | undefined) {
    if (!file || busy) return;
    busy = true;
    pick.disabled = true;
    take.disabled = true;
    said.replaceChildren(`Uploading ${file.name}…`);
    said.className = "small muted";
    meter.hidden = false;
    progress(0);
    const res = await uploadFile(
      file,
      { kind: opts.kind, id: opts.id, fileKind: opts.fileKind },
      progress,
    );
    busy = false;
    pick.disabled = false;
    take.disabled = false;
    picker.value = "";
    shot.value = "";
    if (!res.ok) {
      meter.hidden = true;
      said.className = "small";
      said.replaceChildren(el("span", { class: "chip bad" }, icon("alert"), " ", res.error));
      return;
    }
    said.className = "small muted";
    said.replaceChildren(`Uploaded ${res.file.name}`);
    meter.hidden = true;
    await opts.onUploaded(res.file);
  }

  picker.addEventListener("change", () => void send(picker.files?.[0]));
  shot.addEventListener("change", () => void send(shot.files?.[0]));

  // Drag a file in.
  wrap.addEventListener("dragover", (e) => {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    wrap.classList.add("dropok");
  });
  wrap.addEventListener("dragleave", () => wrap.classList.remove("dropok"));
  wrap.addEventListener("drop", (e) => {
    e.preventDefault();
    wrap.classList.remove("dropok");
    void send(e.dataTransfer?.files?.[0]);
  });

  return { el: wrap, busy: () => busy };
}
